import {
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  List,
  ListItem,
  Row,
  Separator,
  useOverlay,
  H6,
  Button,
} from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { accumulateDestructionReportData, calculateDestructionReport } from '../../../services/userService'
import { capitalizeFirstLetter, getIdElement, isNumber } from '../../../util/StringUtil'
import { UserContext } from '../UserContext'

export default function UserDestructionReportButtonAndDialog() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { currentUser } = useContext(UserContext)
  const overlay = useOverlay()
  const [userDestructionReport, setUserDestructionReport] = useState()

  const closeOverlay = () => {
    setUserDestructionReport()
    overlay.close()
  }

  const fetchAndDisplayData = async (dmcDestructionReportData, i = 1) => {
    try {
      const destructionReportDataResponse = await accumulateDestructionReportData(
        currentUser.contributorId,
        dmcDestructionReportData
      )
      if (destructionReportDataResponse.data.nextPageToken && i < 40) {
        fetchAndDisplayData(destructionReportDataResponse.data, i + 1)
      } else {
        calculateDestructionReport(currentUser.contributorId, destructionReportDataResponse.data).then(
          (destructionReportResult) => {
            setUserDestructionReport(destructionReportResult.data)
            overlay.open()
          }
        )
      }
    } catch (err) {
      error(
        t('user.errors.generatingDestructionReport', {
          defaultValue: `Error generating destruction report: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  const userDetails = currentUser && (
    <>
      <H6>{t('user.destructionReport.userDetails', 'User Details')}</H6>
      <Row guttersY="none">
        <span id="contribId">{`${t('contributorId', 'Contributor ID')}: ${currentUser.contributorId}`}</span>
        <span>{`${t('cisId', 'CIS ID')}: ${getIdElement(currentUser.dmcUser?.cisId)}`}</span>
        <span>{`${t('contactName', 'Contact Name')}: ${currentUser.dmcUser?.contactName}`}</span>
      </Row>
      <Separator size="xs" />
    </>
  )

  const addPersonCounts = () => {
    if (
      userDestructionReport.addPersonCounts !== null &&
      Object.keys(userDestructionReport.addPersonCounts).length > 0
    ) {
      return (
        <>
          <H6>{t('user.destructionReport.addPersonCounts', 'Add Person Counts')}</H6>
          <List>
            {Object.keys(userDestructionReport.addPersonCounts).map((key) => {
              return (
                <ListItem
                  key={key}
                  primaryText={isNumber(key) ? key : capitalizeFirstLetter(key)}
                  endElement={<ListItem.MetaText text={userDestructionReport.addPersonCounts[key]} />}
                />
              )
            })}
          </List>
          <Separator size="xs" />
        </>
      )
    }
    return null
  }

  const attachMemoryCounts = () => {
    if (
      userDestructionReport.attachMemoryCounts !== null &&
      Object.keys(userDestructionReport.attachMemoryCounts).length > 0
    ) {
      return (
        <>
          <H6>{t('user.destructionReport.addMemoryCounts', 'Attach Memory Counts')}</H6>
          <List>
            {Object.keys(userDestructionReport.attachMemoryCounts).map((key) => {
              return (
                <ListItem
                  key={key}
                  primaryText={isNumber(key) ? key : capitalizeFirstLetter(key)}
                  endElement={<ListItem.MetaText text={userDestructionReport.attachMemoryCounts[key]} />}
                />
              )
            })}
          </List>
          <Separator size="xs" />
        </>
      )
    }
    return null
  }

  const recentTransactionSummary = () => {
    return (
      <>
        <H6>{t('user.destructionReport.recentTransactionSummary', 'Recent Transaction Summary')}</H6>
        <Row guttersY="none">
          <span>{`${t('user.mostRecentEdit', 'Most Recent Edit')}: ${userDestructionReport.mostRecentEdit}`}</span>
          <span>{`${t('user.destructiveRatio', 'Destructive Ratio')}: ${
            userDestructionReport.destructiveRatio
          }%`}</span>
          <span>{`${t('user.sampleSizeInYears', 'Sample Size in Years')}: ${
            userDestructionReport.sampleSizeInYears
          }`}</span>
        </Row>
        <Separator size="xs" />
      </>
    )
  }

  const recentTransactionCounts = () => {
    if (
      userDestructionReport.recentTransactionCounts !== null &&
      userDestructionReport.recentTransactionCounts.length > 0
    ) {
      return (
        <>
          <H6>{t('user.destructionReport.recentTransactionCounts', 'Recent Transaction Counts')}</H6>
          <List>
            {userDestructionReport.recentTransactionCounts.map((recentTransactionCount) => {
              const transacationType = Object.keys(recentTransactionCount)[0]
              return (
                <ListItem
                  key={transacationType}
                  primaryText={transacationType}
                  endElement={<ListItem.MetaText text={recentTransactionCount[transacationType]} />}
                />
              )
            })}
          </List>
          <Separator size="xs" />
        </>
      )
    }
    return null
  }

  const otherTopContributorsToSamePersons = () => {
    if (
      userDestructionReport.topContributorsToSamePersons !== null &&
      userDestructionReport.topContributorsToSamePersons.length > 0
    ) {
      return (
        <>
          <H6>
            {t('user.destructionReport.otherTopContributorsToSamePersons', 'Other Top Contributors to Same Persons')}
          </H6>
          <List>
            {userDestructionReport.topContributorsToSamePersons.map((topContributorsToSamePerson) => {
              const contributorId = Object.keys(topContributorsToSamePerson)[0]
              return (
                <ListItem
                  key={contributorId}
                  primaryText={contributorId}
                  endElement={<ListItem.MetaText text={topContributorsToSamePerson[contributorId]} />}
                />
              )
            })}
          </List>
          <Separator size="xs" />
        </>
      )
    }
    return null
  }

  const recentlyEditedPersonsSummary = () => {
    return (
      <>
        <H6>{t('user.destructionReport.recentlyEditedPersonsSummary', 'Recently Edited Persons Summary')}</H6>
        <Row guttersY="none">
          <span>{`${t('user.averageBirthYear', 'Average Birth Year')}: ${
            userDestructionReport.recentlyEditedPersonsAvgBirthYear
          }`}</span>
        </Row>
        <Separator size="xs" />
      </>
    )
  }

  const topRecentBirthPlaceCounts = () => {
    if (
      userDestructionReport.topRecentBirthPlaceCounts !== null &&
      userDestructionReport.topRecentBirthPlaceCounts.length > 0
    ) {
      return (
        <>
          <H6>{t('user.destructionReport.topRecentBirthPlaceCounts', 'Top Recent Birth Place Counts')}</H6>
          <List>
            {userDestructionReport.topRecentBirthPlaceCounts.map((topRecentBirthPlaceCount) => {
              const place = Object.keys(topRecentBirthPlaceCount)[0]
              return (
                <ListItem
                  key={place}
                  primaryText={place === '' ? 'Unknown' : place.replace(/"/g, '')}
                  endElement={<ListItem.MetaText text={topRecentBirthPlaceCount[place]} />}
                />
              )
            })}
          </List>
          <Separator size="xs" />
        </>
      )
    }
    return null
  }

  const recentEthnicityCounts = () => {
    if (
      userDestructionReport.recentEthnicityCounts !== null &&
      userDestructionReport.recentEthnicityCounts.length > 0
    ) {
      return (
        <>
          <H6>{t('user.destructionReport.recentEthnicityCounts', 'Recent Ethnicity Counts')}</H6>
          <List>
            {userDestructionReport.recentEthnicityCounts.map((recentEthnicityCount) => {
              const ethnicity = Object.keys(recentEthnicityCount)[0]
              return (
                <ListItem
                  key={ethnicity}
                  primaryText={ethnicity}
                  endElement={<ListItem.MetaText text={recentEthnicityCount[ethnicity]} />}
                />
              )
            })}
          </List>
          <Separator size="xs" />
        </>
      )
    }
    return null
  }
  return (
    <>
      <Button id="desRptBtn" emphasis="medium" onClick={() => fetchAndDisplayData()}>
        {t('user.destructionReport.destructionReport', 'Destruction Report')}
      </Button>
      <DialogOverlay
        id="destructionOverlay"
        {...overlay}
        title={t('user.destructionReport.destructionReport', 'Destruction Report')}
        size="xxl"
      >
        {userDestructionReport && (
          <>
            {userDetails}
            {addPersonCounts()}
            {attachMemoryCounts()}
            {recentTransactionSummary()}
            {recentTransactionCounts()}
            {otherTopContributorsToSamePersons()}
            {recentlyEditedPersonsSummary()}
            {topRecentBirthPlaceCounts()}
            {recentEthnicityCounts()}
          </>
        )}

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button emphasis="medium" onClick={closeOverlay}>
              {t('action.done', 'Done')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}
